// =============================================================================
// Admin LoginPage — Dark-themed admin authentication
// =============================================================================

import { useState } from 'react';
import { useNavigate, useLocation, Navigate } from 'react-router';
import { useAuth } from '@/hooks/use-auth';
import { Button, Input, Spinner } from '@tkpod/ui';

export function LoginPage() {
  const { login, error, clearError, isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const from = (location.state as { from?: string } | null)?.from ?? '/dashboard';

  // Wait for auth initialization before deciding
  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-900">
        <Spinner size="lg" />
      </div>
    );
  }

  // Already authenticated — redirect away from login
  if (isAuthenticated) {
    return <Navigate to={from} replace />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    setSubmitting(true);

    try {
      await login({ email, password });
      navigate(from, { replace: true });
    } catch {
      // Error shown via store
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-900 px-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="mb-8 text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-brand-600">
            <span className="text-lg font-bold text-white">TK</span>
          </div>
          <h1 className="text-2xl font-bold text-white">Admin Panel</h1>
          <p className="mt-1 text-sm text-slate-400">Transfer Kingdom Platform Administration</p>
        </div>

        <div className="rounded-xl border border-slate-700 bg-slate-800 shadow-xl">
          <form onSubmit={handleSubmit} className="space-y-4 p-6 [&_label]:text-white">
            {error && (
              <div className="rounded-md bg-red-500/10 px-4 py-3 text-sm text-red-400">
                {error}
              </div>
            )}

            <Input
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@transferkingdompod.com"
              required
              autoComplete="email"
              autoFocus
            />

            <Input
              label="Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              required
              autoComplete="current-password"
            />

            <Button
              type="submit"
              className="w-full"
              disabled={submitting || !email || !password}
            >
              {submitting ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>
        </div>

        <p className="mt-6 text-center text-xs text-slate-500">
          &copy; {new Date().getFullYear()} Transfer Kingdom. Platform Admin.
        </p>
      </div>
    </div>
  );
}
